import { BoutiqueActionTypes } from "./constants";
import { BoutiqueReduxState, BoutiqueItem } from "./types";

const SELECTED_BOUTIQUE_KEY = "selectedBoutiqueId";

function readInitialSelectedBoutiqueId(): string | null {
  try {
    const raw = localStorage.getItem(SELECTED_BOUTIQUE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === "string" ? parsed : null;
  } catch {
    return null;
  }
}

const INIT_STATE: BoutiqueReduxState = {
  list: [],
  selectedBoutiqueId: readInitialSelectedBoutiqueId(),
  loading: false,
  loaded: false,
  error: null,
};

export default function boutiqueReducer(
  state: BoutiqueReduxState = INIT_STATE,
  action: { type: string; payload?: any }
): BoutiqueReduxState {
  switch (action.type) {
    case BoutiqueActionTypes.LOAD_BOUTIQUES:
      return { ...state, loading: true, error: null };

    case BoutiqueActionTypes.LOAD_BOUTIQUES_ERROR:
      // Keep any previously loaded list (a transient failure must not wipe it)
      // and surface the error so the page shows a retry, NOT "create your first
      // boutique" — which wrongly tells a seller who HAS boutiques to create one.
      return {
        ...state,
        loading: false,
        loaded: true,
        error: action.payload?.error || "Failed to load boutiques",
      };

    case BoutiqueActionTypes.SET_BOUTIQUES:
      return {
        ...state,
        list: action.payload?.list ?? [],
        loading: false,
        loaded: true,
        error: null,
      };

    case BoutiqueActionTypes.UPDATE_SELECTED_BOUTIQUE:
      return { ...state, selectedBoutiqueId: action.payload ?? null };

    case BoutiqueActionTypes.CREATE_BOUTIQUE:
    case BoutiqueActionTypes.UPDATE_BOUTIQUE:
    case BoutiqueActionTypes.DELETE_BOUTIQUE:
      return { ...state, error: null };

    case BoutiqueActionTypes.CREATE_BOUTIQUE_SUCCESS: {
      const b: BoutiqueItem = action.payload?.boutique;
      if (!b) return state;
      return { ...state, list: [b, ...state.list], error: null };
    }

    case BoutiqueActionTypes.UPDATE_BOUTIQUE_SUCCESS: {
      const patch = action.payload?.boutique;
      if (!patch?.id) return state;
      return {
        ...state,
        list: state.list.map((b) => (b.id === patch.id ? { ...b, ...patch } : b)),
        error: null,
      };
    }

    case BoutiqueActionTypes.DELETE_BOUTIQUE_SUCCESS: {
      const id = action.payload?.id;
      if (!id) return state;
      return {
        ...state,
        list: state.list.filter((b) => b.id !== id),
        selectedBoutiqueId:
          state.selectedBoutiqueId === id ? null : state.selectedBoutiqueId,
        error: null,
      };
    }

    case BoutiqueActionTypes.CREATE_BOUTIQUE_ERROR:
    case BoutiqueActionTypes.UPDATE_BOUTIQUE_ERROR:
    case BoutiqueActionTypes.DELETE_BOUTIQUE_ERROR:
      return { ...state, error: action.payload?.error || "Unknown error" };

    default:
      return state;
  }
}
